import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Star, TrendingUp, Calendar, Users, Award } from 'lucide-react';
import { toast } from 'sonner';

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    ridesCreated: 0,
    ridesJoined: 0,
    ridesCompleted: 0,
  });
  const [recentRides, setRecentRides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`/users/${userId}`);
      setUser(response.data);
      // We can't fetch private stats for other users
      setLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const getAchievements = () => {
    const achievements = [];
    if (user?.avg_rating >= 4.5) achievements.push({ icon: '💎', name: 'Top Rated', desc: '4.5+ star rating' });
    if (user?.total_ratings >= 10) achievements.push({ icon: '⭐', name: 'Experienced', desc: '10+ ratings' });
    if (user?.total_ratings >= 25) achievements.push({ icon: '🏆', name: 'Veteran Cyclist', desc: '25+ ratings' });
    return achievements;
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <p className="text-gray-400">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-xl font-bold text-white">Rider Profile</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Profile Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
              {/* Profile Picture */}
              <div>
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-purple-600"
                  />
                ) : (
                  <div className="w-24 h-24 bg-purple-600 rounded-full flex items-center justify-center border-4 border-purple-500">
                    <span className="text-white text-4xl font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-foreground mb-2">{user.name}</h1>
                <p className="text-muted-foreground mb-3">{user.bio || 'No bio yet'}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">{user.experience_level}</Badge>
                  {user.preferred_distance && (
                    <Badge variant="outline">Prefers {user.preferred_distance}km rides</Badge>
                  )}
                </div>
              </div>

              {/* Rating Display */}
              <div className="text-center bg-accent p-4 rounded-lg">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Star className="w-6 h-6 text-yellow-500" fill="currentColor" />
                  <span className="text-3xl font-bold text-foreground">{user.avg_rating.toFixed(1)}</span>
                </div>
                <p className="text-sm text-muted-foreground">{user.total_ratings} ratings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Achievements */}
        {getAchievements().length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Achievements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {getAchievements().map((achievement, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-accent rounded-lg">
                    <span className="text-3xl">{achievement.icon}</span>
                    <div>
                      <p className="font-medium">{achievement.name}</p>
                      <p className="text-sm text-muted-foreground">{achievement.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Message */}
        <Card className="mt-6">
          <CardContent className="p-6">
            <div className="bg-accent p-4 rounded-md text-center">
              <p className="text-sm text-muted-foreground">
                Join a ride together to start chatting! 🚴
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
